import { ToastPosition } from '@/shared/types/toast';
import { AlertColor, VariantAlert } from '@/shared/types/alert';
import { IconType } from '@/shared/types/types';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCircleCheck,
  faLightEmergencyOn,
  faExclamationTriangle,
  faCircleInfo,
  faShapes,
} from '@fortawesome/pro-regular-svg-icons';
export const useAlertStyles = () => {
  const getAlertClassName = (
    color: AlertColor,
    isLeaving?: boolean,
    variant: VariantAlert = 'contained'
  ) => {
    const baseClasses =
      'ui-rounded-2xl ui-p-2 ui-shadow-02 ui-transform ui-transition-all ui-duration-300 ui-ease-in-out';

    // Animación de entrada/salida
    const animationClasses = isLeaving
      ? 'ui-opacity-0 ui-translate-x-4'
      : 'ui-opacity-100 ui-translate-x-0';

    // Colores según tipo (usando los nuevos valores de AlertColor)
    const typeClasses: Record<AlertColor, Record<VariantAlert, string>> = {
      success: {
        contained: 'ui-bg-success-500',
        outlined: '!ui-bg-fill !ui-text-success-500 ui-border ui-border-success-500 ui-shadow',
        accent: 'ui-bg-success-300 ui-text-success-500 ui-border-l-6 ui-border-success-500',
        minimal: 'ui-bg-fill ui-text-success-500',
      },
      danger: {
        contained: 'ui-bg-danger-500',
        outlined: '!ui-bg-fill !ui-text-danger-500 ui-border ui-border-danger-500 ui-shadow',
        accent: 'ui-bg-danger-300 ui-text-danger-500 ui-border-l-6 ui-border-danger-500',
        minimal: 'ui-bg-fill ui-text-danger-500',
      },
      info: {
        contained: 'ui-bg-info-500',
        outlined: '!ui-bg-fill !ui-text-info-500 ui-border ui-border-info-500 ui-shadow',
        accent: 'ui-bg-info-300 ui-text-info-500 ui-border-l-6 ui-border-info-500',
        minimal: 'ui-bg-fill ui-text-info-500',
      },
      warning: {
        contained: 'ui-bg-warning-500',
        outlined: '!ui-bg-fill !ui-text-warning-500 ui-border ui-border-warning-500 ui-shadow',
        accent: 'ui-bg-warning-300 ui-text-warning-500 ui-border-l-6 ui-border-warning-500',
        minimal: 'ui-bg-fill ui-text-warning-500',
      },
      neutral: {
        contained: 'ui-bg-fill ui-text-secondary',
        outlined: '!ui-bg-fill !ui-text-secondary ui-border ui-border-default ui-shadow',
        accent: 'ui-bg-fill ui-text-secondary ui-border-l-4 ui-border-default',
        minimal: 'ui-bg-fill ui-text-secondary',
      },
      brand: {
        contained: 'ui-bg-fill ui-border-1 ui-border-solid ui-border-default ui-text-brand',
        outlined: 'ui-bg-fill ui-border-1 ui-border-solid ui-border-default ui-text-brand',
        accent: 'ui-bg-fill ui-text-brand ui-border-l-4 ui-border-solid ui-border-default',
        minimal: 'ui-bg-fill ui-text-brand',
      },
    };

    return `${baseClasses} ${animationClasses} ${typeClasses[color][variant]}`;
  };

  const getPositionClasses = (position: ToastPosition) => {
    const positionClasses = {
      'top-left': 'ui-fixed ui-top-5 ui-left-5',
      'top-center': 'ui-fixed ui-top-5 ui-left-1/2 ui-transform ui-translate-x-1/2',
      'top-right': 'ui-fixed ui-top-5 ui-right-5',
      'center-left': 'ui-fixed ui-top-1/2 ui-left-5 ui-transform ui-translate-y-1/2',
      center: 'ui-fixed ui-top-1/2 ui-left-1/2 ui-transform ui-translate-x-1/2 ui-translate-y-1/2',
      'center-right': 'ui-fixed ui-top-1/2 ui-right-5 ui-transform ui-translate-y-1/2',
      'bottom-left': 'ui-fixed ui-bottom-5 ui-left-5',
      'bottom-center': 'ui-fixed ui-bottom-5 ui-left-1/2 ui-transform ui-translate-x-1/2',
      'bottom-right': 'ui-fixed ui-bottom-5 ui-right-5',
    };

    return positionClasses[position];
  };

  // Función para obtener el color de la barra de progreso según el tipo de toast
  const getProgressBarColor = (color: AlertColor, variant: VariantAlert) => {
    const colorMap: Record<AlertColor, string> = {
      success: 'ui-bg-success-500',
      danger: 'ui-bg-danger-500',
      warning: 'ui-bg-warning-500',
      info: 'ui-bg-info-500',
      neutral: 'ui-bg-tertiary',
      brand: 'ui-bg-brand',
    };
    return variant === 'contained' ? 'ui-bg-fill' : colorMap[color];
  };

  const getIconForType = (color: AlertColor): IconType | IconDefinition => {
    const iconMap: Record<AlertColor, IconType | IconDefinition> = {
      success: faCircleCheck,
      danger: faLightEmergencyOn,
      warning: faExclamationTriangle,
      info: faCircleInfo,
      neutral: faShapes,
      brand: faCircleInfo,
    };

    return iconMap[color];
  };

  return {
    getAlertClassName,
    getPositionClasses,
    getIconForType,
    getProgressBarColor,
  };
};
